import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';

import { googleMapsMapId } from '../../Config/env';
import { isGoogleMapsConfigured, loadGoogleMaps } from '../../Services/googleMaps';
import { DEFAULT_FRANCE_CENTER, toCoordinate } from '../../Utils/geo';

const markerVisual = (kind = 'default', selected = false) => {
  const config = {
    artisan: { fill: '#111111', glyph: 'A' },
    demand: { fill: '#b1843d', glyph: 'D' },
    project: { fill: '#2f7a4d', glyph: '✓' },
    default: { fill: '#5d6770', glyph: '•' },
  }[kind] || { fill: '#5d6770', glyph: '•' };
  const size = selected ? 42 : 36;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size + 8}" viewBox="0 0 36 44"><path fill="${config.fill}" stroke="#fff" stroke-width="2" d="M18 1C8.6 1 1 8.6 1 18c0 12.3 17 25 17 25s17-12.7 17-25C35 8.6 27.4 1 18 1z"/><circle cx="18" cy="17" r="10.5" fill="#fff" fill-opacity=".16"/><text x="18" y="21" text-anchor="middle" font-family="Arial,sans-serif" font-size="12" font-weight="900" fill="#fff">${config.glyph}</text></svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size + 8),
    anchor: new window.google.maps.Point(size / 2, size + 7),
  };
};

const GoogleMap = ({
  center,
  markers = [],
  radiusKm = 0,
  zoom = 11,
  activeId = '',
  onMarkerClick,
  draggableMarkerId = '',
  onMarkerDrag,
  ariaLabel = 'Carte',
  className,
}) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRefs = useRef([]);
  const circleRef = useRef(null);
  const [error, setError] = useState('');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isGoogleMapsConfigured || !containerRef.current) return undefined;

    loadGoogleMaps('maps')
      .then((maps) => {
        if (cancelled || !containerRef.current) return;
        const initial = toCoordinate(center) || toCoordinate(markers[0]?.position) || DEFAULT_FRANCE_CENTER;
        mapRef.current = new maps.Map(containerRef.current, {
          center: { lat: initial.latitude, lng: initial.longitude },
          zoom,
          mapId: googleMapsMapId || undefined,
          streetViewControl: false,
          fullscreenControl: false,
          mapTypeControl: false,
          clickableIcons: false,
          gestureHandling: 'greedy',
        });
        setError('');
        setReady(true);
      })
      .catch((mapError) => setError(mapError.message));

    return () => {
      cancelled = true;
      markerRefs.current.forEach((marker) => marker.setMap?.(null));
      markerRefs.current = [];
      circleRef.current?.setMap?.(null);
      circleRef.current = null;
      mapRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const maps = window.google?.maps;
    if (!map || !maps) return;

    markerRefs.current.forEach((marker) => marker.setMap(null));
    markerRefs.current = [];

    const bounds = new maps.LatLngBounds();
    markers.forEach((item, index) => {
      const coordinate = toCoordinate(item.position);
      if (!coordinate) return;
      const selected = String(item.id) === String(activeId);
      const marker = new maps.Marker({
        map,
        position: { lat: coordinate.latitude, lng: coordinate.longitude },
        title: item.label || `Projet ${index + 1}`,
        label: item.priceLabel ? { text: item.priceLabel, color: '#111111', fontWeight: '800' } : undefined,
        icon: item.priceLabel ? undefined : markerVisual(item.kind, selected),
        draggable: String(item.id) === String(draggableMarkerId),
        animation: selected ? maps.Animation.BOUNCE : undefined,
        zIndex: selected ? 50 : 10,
      });
      marker.addListener('click', () => onMarkerClick?.(item));
      if (String(item.id) === String(draggableMarkerId)) {
        marker.addListener('dragend', () => {
          const position = marker.getPosition();
          if (position) onMarkerDrag?.({ latitude: position.lat(), longitude: position.lng() });
        });
      }
      markerRefs.current.push(marker);
      bounds.extend(marker.getPosition());
    });

    const centerCoordinate = toCoordinate(center);
    if (centerCoordinate) {
      bounds.extend({ lat: centerCoordinate.latitude, lng: centerCoordinate.longitude });
      circleRef.current?.setMap?.(null);
      if (radiusKm > 0) {
        circleRef.current = new maps.Circle({
          map,
          center: { lat: centerCoordinate.latitude, lng: centerCoordinate.longitude },
          radius: radiusKm * 1000,
          strokeColor: '#111111',
          strokeOpacity: 0.55,
          strokeWeight: 2,
          fillColor: '#111111',
          fillOpacity: 0.08,
          clickable: false,
        });
      }
    }

    if (markers.length > 1 && !draggableMarkerId) map.fitBounds(bounds, 46);
    else if (centerCoordinate) map.setCenter({ lat: centerCoordinate.latitude, lng: centerCoordinate.longitude });
  }, [activeId, center, draggableMarkerId, markers, onMarkerClick, onMarkerDrag, radiusKm, ready]);

  if (!isGoogleMapsConfigured) {
    return (
      <Fallback className={className} role="status">
        <MapOutlinedIcon />
        <strong>Carte non configurée</strong>
        <span>Ajoutez la clé Google Maps dans <code>.env.local</code>.</span>
      </Fallback>
    );
  }

  return (
    <MapShell className={className} aria-label={ariaLabel}>
      <MapNode ref={containerRef} />
      {error && <ErrorBanner>{error}</ErrorBanner>}
    </MapShell>
  );
};

const MapShell = styled.div`position: relative; width: 100%; min-height: 18rem; overflow: hidden; background: #e9eff3;`;
const MapNode = styled.div`position: absolute; inset: 0;`;
const ErrorBanner = styled.div`position: absolute; left: .75rem; right: .75rem; bottom: .75rem; padding: .65rem .75rem; border-radius: .75rem; background: rgba(132,44,36,.92); color: #fff; font-size: .76rem;`;
const Fallback = styled.div`
  min-height: 18rem; display: grid; place-items: center; align-content: center; gap: .4rem; padding: 1rem; background: linear-gradient(135deg,#f1ece5,#faf8f4); color: #5f7382; text-align: center;
  svg { font-size: 2rem; } strong { color: #23445a; } span { font-size: .78rem; }
`;

export default GoogleMap;
